// Starter configurations - centralized config for each starter
export const STARTER_CONFIGS = {
  law: {
    basePath: "/starters/law",
    font: {
      family: "Lora",
      weights: ["400", "700"],
      variable: "--t-font-family--lora",
    },
    headerButtons: {
      primary: {
        title: "Get Legal Advice",
        destination: "/starters/law/contact",
      },
      clickToCall: {
        title: "(555) 123-4567",
        destination: "tel:+15551234567",
      },
    },
    menu: {
      title: "Law Starter Header",
      items: [
        {
          _key: "home",
          title: "Home",
          _type: "link_list",
          destination: "/",
          uid: "home",
        },
        {
          _key: "about",
          title: "About",
          _type: "link_list",
          destination: "/about",
          uid: "about",
        },
        {
          _key: "services",
          title: "Services",
          _type: "link_list",
          destination: "/services",
          uid: "services",
          links: [
            {
              _key: "service1",
              title: "Service One",
              _type: "link_count_2",
              destination: "/services/service-one",
              uid: "service1",
            },
            {
              _key: "service2",
              title: "Service Two",
              _type: "link_count_2",
              destination: "/services/service-two",
              uid: "service2",
            },
          ],
        },
        {
          _key: "contact",
          title: "Contact",
          _type: "link_list",
          destination: "/contact",
          uid: "contact",
        },
      ],
    },
  },
  medical: {
    basePath: "/starters/medical",
    font: {
      family: "Inter",
      weights: ["400", "600", "700"],
      variable: "--t-font-family--inter",
    },
    menu: {
      title: "Medical Starter Header",
      items: [
        {
          _key: "home",
          title: "Home",
          _type: "link_list",
          destination: "/",
          uid: "home",
        },
        {
          _key: "services",
          title: "Services",
          _type: "link_list",
          destination: "/services",
          uid: "services",
        },
        {
          _key: "contact",
          title: "Contact",
          _type: "link_list",
          destination: "/contact",
          uid: "contact",
        },
      ],
    },
  },
  restaurant: {
    basePath: "/starters/restaurant",
    font: {
      family: "Playfair Display",
      weights: ["400", "700"],
      variable: "--t-font-family--playfair",
    },
    menu: {
      title: "Restaurant Starter Header",
      items: [
        {
          _key: "home",
          title: "Home",
          _type: "link_list",
          destination: "/",
          uid: "home",
        },
        {
          _key: "menu",
          title: "Menu",
          _type: "link_list",
          destination: "/menu",
          uid: "menu",
        },
        {
          _key: "contact",
          title: "Contact",
          _type: "link_list",
          destination: "/contact",
          uid: "contact",
        },
      ],
    },
  },
  // Add more starters here...
};

// Helper function to prefix all destinations with base path
const prefixMenuDestinations = (items, basePath) => {
  return items.map((item) => ({
    ...item,
    destination:
      item.destination === "/" ? basePath : `${basePath}${item.destination}`,
    links: item.links
      ? prefixMenuDestinations(item.links, basePath)
      : undefined,
  }));
};

// Get starter configuration by slug
export const getStarterConfig = (pathname) => {
  if (!pathname?.startsWith("/starters/")) return null;

  // Extract starter slug from pathname (e.g., "/starters/law/about" -> "law")
  const match = pathname.match(/\/starters\/([^\/]+)/);
  if (!match || !match[1]) return null;

  const starterSlug = match[1];
  const config = STARTER_CONFIGS[starterSlug];

  if (!config) return null;

  return {
    basePath: config.basePath,
    headerButtons: config.headerButtons,
    menu: {
      ...config.menu,
      items: prefixMenuDestinations(config.menu.items, config.basePath),
    },
  };
};

export const STARTER_LOGO_SVG = () => (
  <div className="w-[170px]">
    <svg
      className="w-[100%]"
      width="222"
      height="40"
      viewBox="0 0 222 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20 0C27.4768 0 31.2154 -0.000204921 34 1.60742C35.8242 2.66064 37.3394 4.17577 38.3926 6C40.0002 8.7846 40 12.5232 40 20C40 27.4768 40.0002 31.2154 38.3926 34C37.3394 35.8242 35.8242 37.3394 34 38.3926C31.2154 40.0002 27.4768 40 20 40C12.5232 40 8.7846 40.0002 6 38.3926C4.17577 37.3394 2.66064 35.8242 1.60742 34C-0.000204921 31.2154 0 27.4768 0 20C0 12.5232 -0.000204921 8.7846 1.60742 6C2.66064 4.17577 4.17577 2.66064 6 1.60742C8.7846 -0.000204921 12.5232 0 20 0ZM22 4C13.1634 4 6 11.1634 6 20C6 28.8366 13.1634 36 22 36C30.8366 36 38 28.8366 38 20C38 11.1634 30.8366 4 22 4Z"
        fill="var(--t-primary-branding-color, #FF4D00)"
      />
      <path
        d="M36 20C36 25.5228 31.5228 30 26 30C20.4772 30 16 25.5228 16 20C16 14.4772 20.4772 10 26 10C31.5228 10 36 14.4772 36 20Z"
        fill="var(--t-primary-branding-color, #FF4D00)"
      />
      <path
        d="M214.535 11.2587C216.771 11.2587 218.569 11.9453 219.928 13.3186C221.309 14.67 222 16.5665 222 19.0078V29.6343H217.396V19.6291C217.396 18.2122 217.034 17.1332 216.311 16.3921C215.587 15.6292 214.6 15.2477 213.351 15.2477C212.101 15.2477 211.103 15.6292 210.358 16.3921C209.634 17.1332 209.273 18.2122 209.273 19.6291V29.6343H204.668V19.6291C204.668 18.2122 204.307 17.1332 203.583 16.3921C202.86 15.6292 201.873 15.2477 200.623 15.2477C199.352 15.2477 198.343 15.6292 197.598 16.3921C196.874 17.1332 196.512 18.2122 196.512 19.6291V29.6343H191.908V11.5203H196.512V13.7109C197.104 12.948 197.861 12.3486 198.782 11.9126C199.724 11.4767 200.755 11.2587 201.873 11.2587C203.298 11.2587 204.57 11.5639 205.688 12.1742C206.806 12.7627 207.672 13.6128 208.286 14.7245C208.878 13.6782 209.733 12.839 210.851 12.2069C211.991 11.5747 213.219 11.2587 214.535 11.2587Z"
        fill="#391300"
      />
      <path
        d="M188.516 11.5202V29.6343H183.879V27.3455C183.287 28.1302 182.509 28.7514 181.544 29.2092C180.602 29.6452 179.571 29.8631 178.453 29.8631C177.028 29.8631 175.767 29.5689 174.671 28.9803C173.575 28.37 172.709 27.4872 172.073 26.3319C171.459 25.1548 171.152 23.7597 171.152 22.1467V11.5202H175.756V21.4928C175.756 22.9314 176.118 24.0431 176.841 24.8278C177.565 25.5908 178.552 25.9722 179.801 25.9722C181.073 25.9722 182.071 25.5908 182.794 24.8278C183.518 24.0431 183.879 22.9314 183.879 21.4928V11.5202H188.516Z"
        fill="#391300"
      />
      <path
        d="M161.519 29.9285C160.028 29.9285 158.69 29.667 157.506 29.1438C156.322 28.5989 155.38 27.8686 154.678 26.9531C153.998 26.0376 153.626 25.024 153.56 23.9123H158.197C158.285 24.6099 158.625 25.1875 159.216 25.6453C159.83 26.103 160.587 26.3319 161.486 26.3319C162.363 26.3319 163.042 26.1575 163.525 25.8088C164.029 25.46 164.281 25.0131 164.281 24.4682C164.281 23.8796 163.974 23.4437 163.36 23.1603C162.768 22.8551 161.815 22.5282 160.499 22.1794C159.14 21.8524 158.022 21.5146 157.145 21.1658C156.29 20.817 155.544 20.283 154.908 19.5637C154.294 18.8443 153.987 17.8743 153.987 16.6537C153.987 15.6509 154.272 14.7354 154.843 13.9071C155.434 13.0788 156.268 12.4249 157.342 11.9453C158.438 11.4658 159.721 11.226 161.19 11.226C163.36 11.226 165.092 11.7709 166.386 12.8608C167.679 13.9289 168.392 15.3785 168.524 17.2095H164.117C164.051 16.4902 163.744 15.9234 163.196 15.5093C162.67 15.0733 161.957 14.8553 161.058 14.8553C160.225 14.8553 159.578 15.0079 159.118 15.3131C158.679 15.6183 158.46 16.0433 158.46 16.5883C158.46 17.1986 158.767 17.6673 159.381 17.9942C159.995 18.2994 160.949 18.6155 162.242 18.9424C163.558 19.2694 164.643 19.6073 165.498 19.956C166.353 20.3048 167.088 20.8497 167.701 21.5909C168.337 22.3102 168.666 23.2693 168.688 24.4682C168.688 25.5145 168.392 26.4518 167.8 27.2801C167.23 28.1084 166.397 28.7624 165.301 29.2419C164.226 29.6997 162.966 29.9285 161.519 29.9285Z"
        fill="#391300"
      />
      <path
        d="M138.013 14.136C138.605 13.3077 139.416 12.621 140.447 12.0761C141.499 11.5094 142.694 11.226 144.031 11.226C145.588 11.226 146.991 11.6074 148.241 12.3704C149.512 13.1333 150.51 14.2232 151.234 15.64C151.979 17.0351 152.352 18.6591 152.352 20.5119C152.352 22.3647 151.979 24.0104 151.234 25.4491C150.51 26.8659 149.512 27.9667 148.241 28.7515C146.991 29.5362 145.588 29.9285 144.031 29.9285C142.694 29.9285 141.51 29.6561 140.479 29.1111C139.471 28.5662 138.649 27.8795 138.013 27.0512V38.2662H133.409V11.5203H138.013V14.136ZM147.649 20.5119C147.649 19.422 147.419 18.4847 146.958 17.6999C146.52 16.8934 145.928 16.2831 145.182 15.8689C144.459 15.4548 143.67 15.2477 142.814 15.2477C141.981 15.2477 141.192 15.4657 140.447 15.9016C139.723 16.3158 139.131 16.9261 138.671 17.7326C138.232 18.5392 138.013 19.4874 138.013 20.5773C138.013 21.6672 138.232 22.6154 138.671 23.4219C139.131 24.2284 139.723 24.8496 140.447 25.2856C141.192 25.6998 141.981 25.9068 142.814 25.9068C143.67 25.9068 144.459 25.6889 145.182 25.2529C145.928 24.8169 146.52 24.1957 146.958 23.3892C147.419 22.5827 147.649 21.6236 147.649 20.5119Z"
        fill="#391300"
      />
      <path
        d="M127.742 9.36227C126.93 9.36227 126.251 9.1116 125.703 8.61025C125.176 8.0871 124.913 7.44406 124.913 6.68114C124.913 5.91821 125.176 5.28607 125.703 4.78472C126.251 4.26157 126.93 4 127.742 4C128.553 4 129.222 4.26157 129.748 4.78472C130.296 5.28607 130.57 5.91821 130.57 6.68114C130.57 7.44406 130.296 8.0871 129.748 8.61025C129.222 9.1116 128.553 9.36227 127.742 9.36227ZM130.011 11.5203V29.6343H125.407V11.5203H130.011Z"
        fill="#391300"
      />
      <path
        d="M113.676 29.9285C111.922 29.9285 110.343 29.5471 108.94 28.7842C107.537 27.9994 106.43 26.8986 105.618 25.4818C104.829 24.0649 104.435 22.4301 104.435 20.5773C104.435 18.7244 104.84 17.0896 105.651 15.6727C106.485 14.2559 107.614 13.166 109.039 12.4031C110.464 11.6183 112.053 11.226 113.807 11.226C115.561 11.226 117.151 11.6183 118.576 12.4031C120.001 13.166 121.119 14.2559 121.931 15.6727C122.764 17.0896 123.18 18.7244 123.18 20.5773C123.18 22.4301 122.753 24.0649 121.898 25.4818C121.065 26.8986 119.924 27.9994 118.477 28.7842C117.052 29.5471 115.452 29.9285 113.676 29.9285ZM113.676 25.9395C114.509 25.9395 115.287 25.7434 116.011 25.351C116.756 24.9368 117.348 24.3265 117.787 23.52C118.225 22.7135 118.445 21.7326 118.445 20.5773C118.445 18.8552 117.984 17.5365 117.063 16.621C116.164 15.6836 115.057 15.215 113.742 15.215C112.426 15.215 111.319 15.6836 110.42 16.621C109.543 17.5365 109.105 18.8552 109.105 20.5773C109.105 22.2993 109.532 23.629 110.387 24.5663C111.264 25.4818 112.36 25.9395 113.676 25.9395Z"
        fill="#391300"
      />
      <path
        d="M93.8549 29.9285C92.1009 29.9285 90.5223 29.5471 89.1191 28.7842C87.716 27.9994 86.6088 26.8986 85.7975 25.4818C85.0082 24.0649 84.6136 22.4301 84.6136 20.5773C84.6136 18.7244 85.0192 17.0896 85.8304 15.6727C86.6636 14.2559 87.7927 13.166 89.2178 12.4031C90.6429 11.6183 92.2325 11.226 93.9865 11.226C95.7404 11.226 97.33 11.6183 98.7551 12.4031C100.18 13.166 101.298 14.2559 102.11 15.6727C102.943 17.0896 103.359 18.7244 103.359 20.5773C103.359 22.4301 102.932 24.0649 102.077 25.4818C101.244 26.8986 100.103 27.9994 98.6564 28.7842C97.2313 29.5471 95.6308 29.9285 93.8549 29.9285ZM93.8549 25.9395C94.688 25.9395 95.4664 25.7434 96.1899 25.351C96.9353 24.9368 97.5273 24.3265 97.9658 23.52C98.4043 22.7135 98.6236 21.7326 98.6236 20.5773C98.6236 18.8552 98.1631 17.5365 97.2423 16.621C96.3434 15.6836 95.2362 15.215 93.9207 15.215C92.6052 15.215 91.498 15.6836 90.5991 16.621C89.7221 17.5365 89.2836 18.8552 89.2836 20.5773C89.2836 22.2993 89.7111 23.629 90.5662 24.5663C91.4432 25.4818 92.5394 25.9395 93.8549 25.9395Z"
        fill="#391300"
      />
      <path
        d="M74.0339 29.9285C72.28 29.9285 70.7014 29.5471 69.2982 28.7842C67.895 27.9994 66.7878 26.8986 65.9766 25.4818C65.1873 24.0649 64.7926 22.4301 64.7926 20.5773C64.7926 18.7244 65.1982 17.0896 66.0095 15.6727C66.8426 14.2559 67.9717 13.166 69.3968 12.4031C70.822 11.6183 72.4115 11.226 74.1655 11.226C75.9195 11.226 77.509 11.6183 78.9341 12.4031C80.3593 13.166 81.4774 14.2559 82.2886 15.6727C83.1218 17.0896 83.5384 18.7244 83.5384 20.5773C83.5384 22.4301 83.1108 24.0649 82.2558 25.4818C81.4226 26.8986 80.2825 27.9994 78.8355 28.7842C77.4104 29.5471 75.8099 29.9285 74.0339 29.9285ZM74.0339 25.9395C74.8671 25.9395 75.6454 25.7434 76.3689 25.351C77.1144 24.9368 77.7064 24.3265 78.1448 23.52C78.5833 22.7135 78.8026 21.7326 78.8026 20.5773C78.8026 18.8552 78.3422 17.5365 77.4213 16.621C76.5224 15.6836 75.4152 15.215 74.0997 15.215C72.7842 15.215 71.677 15.6836 70.7781 16.621C69.9011 17.5365 69.4626 18.8552 69.4626 20.5773C69.4626 22.2993 69.8902 23.629 70.7452 24.5663C71.6222 25.4818 72.7185 25.9395 74.0339 25.9395Z"
        fill="#391300"
      />
      <path
        d="M56.6042 26.0049H64.1683V29.6342H52V6.8119H56.6042V26.0049Z"
        fill="#391300"
      />
      <path
        d="M98.3243 30.6895C98.6953 30.5407 99.0584 30.3715 99.4132 30.1815L99.4208 30.1774C100.987 29.3281 102.257 28.1549 103.213 26.6755V30.2702C103.111 35.1089 99.1495 39 94.278 39H93.7878C88.9163 39 84.9552 35.1089 84.8532 30.2702H89.7005C89.8006 32.4373 91.5923 34.1635 93.7878 34.1635H94.278C96.3299 34.1635 98.0291 32.6557 98.3243 30.6895Z"
        fill="#391300"
      />
    </svg>
  </div>
);

// Helper to check if current path is a starter site
export const isStarterSite = (pathname) => {
  if (typeof window !== "undefined") {
    return window.location.pathname.includes("/starters/");
  }
  return pathname?.includes("/starters/");
};
